import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './BirthDetailsPage.css' // Предполагается, что будет создан CSS файл

interface BirthDetailsPageProps {
  updateUserData: (category: string, data: any) => void
}

interface FormDataType {
  birthDate: string
  timeSelectionType: 'exact' | 'range' | 'unknown'
  exactTime: string
  timeRange: string
}

interface BirthDetailsErrors {
  birthDate?: string
  exactTime?: string
  timeRange?: string
}

const BirthDetailsPage = ({ updateUserData }: BirthDetailsPageProps) => {
  const [formData, setFormData] = useState<FormDataType>({
    birthDate: '',
    timeSelectionType: 'exact',
    exactTime: '',
    timeRange: '',
  })
  const [errors, setErrors] = useState<BirthDetailsErrors>({})
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  // Обработчик изменения типа выбора времени
  const handleTimeSelectionChange = (type) => {
    setFormData((prevData) => ({
      ...prevData,
      timeSelectionType: type,
      // Сбрасываем другие значения времени при переключении
      exactTime: type === 'exact' ? prevData.exactTime : '',
      timeRange: type === 'range' ? prevData.timeRange : '',
    }))
  }

  const validateForm = () => {
    const newErrors: BirthDetailsErrors = {}

    if (!formData.birthDate) {
      newErrors.birthDate = 'Пожалуйста, введите дату рождения'
    }

    // Валидация в зависимости от выбранного типа времени
    if (formData.timeSelectionType === 'exact' && !formData.exactTime) {
      newErrors.exactTime = 'Пожалуйста, введите точное время рождения'
    } else if (formData.timeSelectionType === 'range' && !formData.timeRange) {
      newErrors.timeRange = 'Пожалуйста, выберите примерный диапазон времени'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Получение времени для API в зависимости от выбранного типа
  const getTimeForAPI = () => {
    if (formData.timeSelectionType === 'exact' && formData.exactTime) {
      return formData.exactTime
    } else if (formData.timeSelectionType === 'range' && formData.timeRange) {
      // Преобразуем диапазон в конкретное время для API
      switch (formData.timeRange) {
        case '06:00-09:00':
          return '07:30'
        case '09:00-15:00':
          return '12:00'
        case '15:00-21:00':
          return '18:00'
        case '21:00-06:00':
          return '00:00'
        default:
          return '12:00'
      }
    } else {
      // Если тип 'unknown' или если данные некорректны
      return '12:00'
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      // Формируем данные для сохранения
      const birthDetailsForSave = {
        birthDate: formData.birthDate,
        timeSelectionType: formData.timeSelectionType,
        exactTime: formData.exactTime,
        timeRange: formData.timeRange,
        timeForAPI: getTimeForAPI(),
      }

      // Сохраняем в localStorage для возможного использования в других местах
      localStorage.setItem('birthDetails', JSON.stringify(birthDetailsForSave))

      // Обновляем данные в родительском компоненте
      updateUserData('birthDetails', birthDetailsForSave)

      // Переходим к следующему шагу
      navigate('/birth-location')
    }
  }

  return (
    <div className="container">
      <div className="header">
        <div className="logo">
          Business <span>Evolution AI</span>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-bar-inner" style={{ width: '50%' }}></div>
      </div>

      <h2>Данные рождения</h2>

      <div className="info-box">
        <p>
          С научной точки зрения хронобиологические особенности периода рождения могут отражаться на
          индивидуальных циркадных ритмах, влияющих на поведенческие паттерны и когнитивные функции
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="birthDate">Дата рождения:</label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleChange}
            className={errors.birthDate ? 'error-input' : ''}
          />
          {errors.birthDate && <div className="error">{errors.birthDate}</div>}
        </div>

        <div className="time-selection-container">
          <h3>Время рождения</h3>

          <div className="time-selection-options">
            <div
              className={`time-option ${formData.timeSelectionType === 'exact' ? 'selected' : ''}`}
              onClick={() => handleTimeSelectionChange('exact')}
            >
              <div className="time-option-radio">
                <input
                  type="radio"
                  name="timeSelectionType"
                  value="exact"
                  checked={formData.timeSelectionType === 'exact'}
                  onChange={() => handleTimeSelectionChange('exact')}
                />
                <label>Я знаю точное время</label>
              </div>

              {formData.timeSelectionType === 'exact' && (
                <div className="time-option-content">
                  <input
                    type="time"
                    id="exactTime"
                    name="exactTime"
                    value={formData.exactTime}
                    onChange={handleChange}
                    className={errors.exactTime ? 'error-input' : ''}
                  />
                  {errors.exactTime && <div className="error">{errors.exactTime}</div>}
                </div>
              )}
            </div>

            <div
              className={`time-option ${formData.timeSelectionType === 'range' ? 'selected' : ''}`}
              onClick={() => handleTimeSelectionChange('range')}
            >
              <div className="time-option-radio">
                <input
                  type="radio"
                  name="timeSelectionType"
                  value="range"
                  checked={formData.timeSelectionType === 'range'}
                  onChange={() => handleTimeSelectionChange('range')}
                />
                <label>Я знаю примерное время (диапазон)</label>
              </div>

              {formData.timeSelectionType === 'range' && (
                <div className="time-option-content">
                  <div className="time-ranges">
                    <div className="time-range-option">
                      <input
                        type="radio"
                        id="timeRange1"
                        name="timeRange"
                        value="06:00-09:00"
                        checked={formData.timeRange === '06:00-09:00'}
                        onChange={handleChange}
                      />
                      <label htmlFor="timeRange1">
                        06:00 – 09:00 <span>(Пробуждение: кортизол, энергия)</span>
                      </label>
                    </div>

                    <div className="time-range-option">
                      <input
                        type="radio"
                        id="timeRange2"
                        name="timeRange"
                        value="09:00-15:00"
                        checked={formData.timeRange === '09:00-15:00'}
                        onChange={handleChange}
                      />
                      <label htmlFor="timeRange2">
                        09:00 – 15:00 <span>(Активность: серотонин, концентрация)</span>
                      </label>
                    </div>

                    <div className="time-range-option">
                      <input
                        type="radio"
                        id="timeRange3"
                        name="timeRange"
                        value="15:00-21:00"
                        checked={formData.timeRange === '15:00-21:00'}
                        onChange={handleChange}
                      />
                      <label htmlFor="timeRange3">
                        15:00 – 21:00 <span>(Продуктивность: дофамин, креативность)</span>
                      </label>
                    </div>

                    <div className="time-range-option">
                      <input
                        type="radio"
                        id="timeRange4"
                        name="timeRange"
                        value="21:00-06:00"
                        checked={formData.timeRange === '21:00-06:00'}
                        onChange={handleChange}
                      />
                      <label htmlFor="timeRange4">
                        21:00 – 06:00 <span>(Отдых: мелатонин, восстановление)</span>
                      </label>
                    </div>
                  </div>
                  {errors.timeRange && <div className="error">{errors.timeRange}</div>}
                </div>
              )}
            </div>

            <div
              className={`time-option ${formData.timeSelectionType === 'unknown' ? 'selected' : ''}`}
              onClick={() => handleTimeSelectionChange('unknown')}
            >
              <div className="time-option-radio">
                <input
                  type="radio"
                  name="timeSelectionType"
                  value="unknown"
                  checked={formData.timeSelectionType === 'unknown'}
                  onChange={() => handleTimeSelectionChange('unknown')}
                />
                <label>Не знаю даже примерного диапазона</label>
              </div>

              {formData.timeSelectionType === 'unknown' && (
                <div className="time-option-content">
                  <p className="note">
                    Не переживайте, мы проведём анализ с использованием доступных данных.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <button type="submit" className="btn">
          Продолжить
        </button>
      </form>
    </div>
  )
}

export default BirthDetailsPage
